import React from 'react';
import { Record } from 'immutable';
import { useLocation, useHistory, matchPath } from 'react-router';

// Hooks
import { useMeetingItemQueryParam } from 'Shared/components/MeetingItemDetailPanel/useMeetingItemQueryParam';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Utils
import * as MeetingItemUtils from 'Shared/utils/MeetingItem.utils';
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Components
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import MeetingItemActionsDropdown from '../../MeetingItemActionsDropdown/MeetingItemActionsDropdown';

// Styles
import './MeetingItemDetailsPanelHeader.scss';

type Props = {
  meetingItem: Record<MeetingItem>;
  fileUploaderRef?: { current: HTMLInputElement };
  handleClosePanel?: Function;
};

const MeetingItemDetailsPanelHeader = ({ meetingItem, handleClosePanel, fileUploaderRef }: Props) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { tr } = useTranslation();

  const itemType = MeetingItemHelpers.getItemType(meetingItem);
  const workspaceId = MeetingItemHelpers.getWorkspaceId(meetingItem);
  const associatedMeetingItemUuid = MeetingItemHelpers.getAssociatedMeetingItemUuid(meetingItem);
  const isActionItem = MeetingItemUtils.isActionItem(itemType);

  // we use query params to hide some details while in meeting mode
  const shouldShowTitleActions = useMeetingItemQueryParam({ paramKey: 'showTitleActions', defaultValue: true });

  const handleAttachFileClick = () => {
    fileUploaderRef.current.click();
  };

  const handleBackClick = () => {
    // unfortunelty we need to redirect to the workspace page
    // because the action item list is dependant on state.workspace.action_items
    history.push(PageUrlsUtil.makeWorkspaceMeetingItemCommentsUrl(workspaceId, associatedMeetingItemUuid));
  };

  const isMeetingMode = !!matchPath(pathname, { path: PageUrls.WORKSPACE_MEETING_MODE_ITEM });
  const shouldShowBackButton = isActionItem && associatedMeetingItemUuid && !isMeetingMode;

  const baseClassName = 'MeetingItemDetailsPanelHeader';
  return (
    <>
      <div className={baseClassName}>
        <WpButtonGroup spacing="lg">
          {shouldShowBackButton && (
            <WpButton variant="tertiary" size="sm" onClick={handleBackClick}>
              <WpIcon name="angle-left" />
              {tr('perform.meeting_item.detail_panel.header.topic_button')}
            </WpButton>
          )}
          {shouldShowTitleActions && (
            <MeetingItemActionsDropdown
              meetingItem={meetingItem}
              handleAttachFileClick={handleAttachFileClick}
              handleClosePanel={handleClosePanel}
            />
          )}
        </WpButtonGroup>

        {handleClosePanel && (
          <WpButton
            variant="tertiary"
            onClick={() => handleClosePanel()}
            qaTest="MeetingItemDetailsPanelHeader-close-btn"
          >
            <WpIcon name="times" />
          </WpButton>
        )}
      </div>
    </>
  );
};

export default MeetingItemDetailsPanelHeader;
